import { useState } from "react";
import { DatePicker } from "@freightsimple/ui-core/DateTime/Controls/DatePicker";
import { Form } from "@freightsimple/ui-core/FormLayout/Form";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { TextArea } from "@freightsimple/ui-core/Controls/TextArea";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { YesNoAny } from "@freightsimple/ui-core/Controls/YesNoAny";
import {
  useFormValidation,
  type ValidatorMap,
} from "@freightsimple/ui-core/Hooks/useFormValidation";
import { toast } from "sonner";
import { type LocalDate } from "@freightsimple/time/branded-types";
import { type CarrierIssueType } from "@freightsimple/generated-carriers-openapi-client/models/CarrierIssueType";
import { type OperationalIssue } from "@freightsimple/generated-carriers-openapi-client/models/OperationalIssue";
import { carriersApi } from "@/App/Auth/carrierApi";
import { describeIssueType } from "@/App/Views/carrierFormatting";
import {
  ISSUE_FIELD_LABELS,
  type IssueField,
  type IssueQuestion,
} from "@/App/Shipment/issueQuestions";

interface ResponseDraft {
  pickupConfirmationNumber: string;
  pickupDate: LocalDate | undefined;
  expectedDeliveryDate: LocalDate | undefined;
  currentStatus: string;
  currentLocation: string;
  freightFound: boolean | undefined;
  reason: string;
  customerVisibleNote: string;
  internalNote: string;
}

const EMPTY_DRAFT: ResponseDraft = {
  pickupConfirmationNumber: "",
  pickupDate: undefined,
  expectedDeliveryDate: undefined,
  currentStatus: "",
  currentLocation: "",
  freightFound: undefined,
  reason: "",
  customerVisibleNote: "",
  internalNote: "",
};

// A required field is only satisfied by a real answer. `freightFound` is the odd
// one: "no, still looking" is a legitimate thing to tell us, but it isn't an
// answer to "where is the freight", so it doesn't satisfy the requirement.
function isAnswered(draft: ResponseDraft, field: IssueField): boolean {
  switch (field) {
    case "pickupDate":
      return draft.pickupDate !== undefined;
    case "expectedDeliveryDate":
      return draft.expectedDeliveryDate !== undefined;
    case "freightFound":
      return draft.freightFound === true;
    case "pickupConfirmationNumber":
      return draft.pickupConfirmationNumber.trim() !== "";
    case "currentStatus":
      return draft.currentStatus.trim() !== "";
    case "currentLocation":
      return draft.currentLocation.trim() !== "";
    case "reason":
      return draft.reason.trim() !== "";
  }
}

/**
 * The carrier's answer to one open issue.
 *
 * The form only asks for what the issue is actually about — a missed pickup
 * asks for the new date, nothing else. Required fields aren't enforced here:
 * "we're still looking for it" is a real and useful answer, so the form always
 * submits. The backend decides whether the answer closes the issue, and says
 * what is still outstanding if it doesn't.
 */
export function IssueResponseForm({
  issue,
  question,
  open,
  onOpenChange,
  onResponded,
}: {
  issue: OperationalIssue;
  question: IssueQuestion;
  open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  onResponded: () => void;
}) {
  const [draft, setDraft] = useState<ResponseDraft>(EMPTY_DRAFT);
  const update = (patch: Partial<ResponseDraft>) =>
    setDraft((previous) => ({ ...previous, ...patch }));

  // Nothing is hard-required, but an entirely blank submission tells us nothing
  // at all, so that one case is worth catching before the round-trip.
  const validators: ValidatorMap<ResponseDraft> = {
    internalNote: () =>
      question.fields.some((field) => isAnswered(draft, field)) ||
      draft.customerVisibleNote.trim() !== "" ||
      draft.internalNote.trim() !== ""
        ? undefined
        : "Tell us something before submitting.",
  };
  const { validation } = useFormValidation(draft, validators);

  async function save() {
    // Only send what the carrier actually filled in, so a blank box never lands
    // as an empty string on the shipment.
    const confirmationNumber = draft.pickupConfirmationNumber.trim();
    const currentStatus = draft.currentStatus.trim();
    const currentLocation = draft.currentLocation.trim();
    const reason = draft.reason.trim();
    const customerVisibleNote = draft.customerVisibleNote.trim();
    const internalNote = draft.internalNote.trim();

    const result = await carriersApi().respondToCarrierOperationalIssue({
      operationalIssueResponseRequest: {
        shipmentId: issue.shipmentId,
        issueType: issue.issueType,
        ...(confirmationNumber
          ? { pickupConfirmationNumber: confirmationNumber }
          : {}),
        ...(draft.pickupDate ? { pickupDate: draft.pickupDate } : {}),
        ...(draft.expectedDeliveryDate
          ? { expectedDeliveryDate: draft.expectedDeliveryDate }
          : {}),
        ...(currentStatus ? { currentStatus } : {}),
        ...(currentLocation ? { currentLocation } : {}),
        ...(draft.freightFound === undefined
          ? {}
          : { freightFound: draft.freightFound }),
        ...(reason ? { reason } : {}),
        ...(customerVisibleNote ? { customerVisibleNote } : {}),
        ...(internalNote ? { internalNote } : {}),
      },
    });
    if (result.taskClosed) {
      toast.success("Thanks — that's resolved.");
    } else {
      toast.info(
        result.outstanding
          ? `Recorded. We still need ${result.outstanding}.`
          : "Recorded. We'll follow up.",
      );
    }
    onResponded();
  }

  return (
    <Form
      open={open}
      onOpenChange={onOpenChange}
      title={describeIssueType(issue.issueType)}
      description={question.prompt}
      validation={validation}
      onSave={save}
      saveText="Submit"
    >
      {question.fields.map((field) => (
        <IssueFieldInput_
          key={field}
          field={field}
          draft={draft}
          onChange={update}
          issueType={issue.issueType}
        />
      ))}
      <FormItem
        label="Update for the customer"
        description="Posted to the customer's tracking straight away."
      >
        <TextArea
          value={draft.customerVisibleNote}
          onChange={(e) => update({ customerVisibleNote: e.target.value })}
          placeholder="e.g. On the truck for delivery tomorrow morning"
          rows={2}
        />
      </FormItem>
      <FormItem
        label="Note for FreightSimple"
        description="Only we see this — the customer never does."
      >
        <TextArea
          value={draft.internalNote}
          onChange={(e) => update({ internalNote: e.target.value })}
          rows={2}
        />
      </FormItem>
    </Form>
  );
}

function IssueFieldInput_({
  field,
  draft,
  onChange,
  issueType,
}: {
  field: IssueField;
  draft: ResponseDraft;
  onChange: (patch: Partial<ResponseDraft>) => void;
  issueType: CarrierIssueType;
}) {
  const label = ISSUE_FIELD_LABELS[field];

  switch (field) {
    case "pickupConfirmationNumber":
      return (
        <FormItem label={label}>
          <TextInput
            value={draft.pickupConfirmationNumber}
            onChange={(e) =>
              onChange({ pickupConfirmationNumber: e.target.value })
            }
          />
        </FormItem>
      );
    case "pickupDate":
      return (
        <FormItem label={label}>
          <DatePicker
            value={draft.pickupDate}
            onChange={(date) => onChange({ pickupDate: date })}
            allowClear
          />
        </FormItem>
      );
    case "expectedDeliveryDate":
      return (
        <FormItem label={label}>
          <DatePicker
            value={draft.expectedDeliveryDate}
            onChange={(date) => onChange({ expectedDeliveryDate: date })}
            allowClear
          />
        </FormItem>
      );
    case "currentStatus":
      return (
        <FormItem
          label={label}
          description="This becomes the customer's tracking update."
        >
          <TextInput
            value={draft.currentStatus}
            onChange={(e) => onChange({ currentStatus: e.target.value })}
            placeholder="e.g. On hand at terminal"
            width="full"
          />
        </FormItem>
      );
    case "currentLocation":
      return (
        <FormItem label={label}>
          <TextInput
            value={draft.currentLocation}
            onChange={(e) => onChange({ currentLocation: e.target.value })}
            placeholder="e.g. Calgary, AB"
            width="full"
          />
        </FormItem>
      );
    case "freightFound":
      return (
        <FormItem label={label}>
          <YesNoAny
            value={draft.freightFound}
            onChange={(value) => onChange({ freightFound: value })}
          />
        </FormItem>
      );
    case "reason":
      return (
        <FormItem label={reasonLabelFor_(issueType) ?? label}>
          <TextArea
            value={draft.reason}
            onChange={(e) => onChange({ reason: e.target.value })}
            rows={2}
          />
        </FormItem>
      );
  }
}

// "What happened?" is right for a missed pickup and wrong for a search that is
// still running, so the one shared field gets an issue-appropriate label.
function reasonLabelFor_(issueType: CarrierIssueType): string | undefined {
  return issueType === "missing_freight" ? "Where have you looked?" : undefined;
}
